
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin, of, Subject, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Role } from '../../../core/models/role.model';
import { RoleService } from '../../../core/services/role.service';
import { ClientService } from '../../../core/services/client.service';
import { NotificationService } from '../../../core/services/notification.service';
import { RolePermissionService, AdminProfile } from '../../../core/services/role-permission.service';
import { User } from '../../../core/models/user.model';

interface Application {
  id: string;
  name: string;
  icon?: string;
  description?: string;
  /** Rôles visibles pour l'admin connecté (déjà filtrés par le backend) */
  roles?: Role[];
  expanded?: boolean;
}

interface RoleSelection {
  [appId: string]: { [roleId: string]: boolean };
}

interface PendingChange {
  appId: string;
  roleId: string;
  action: 'assign' | 'unassign';
  initialState: boolean;
  currentState: boolean;
}

export interface UserRolesDialogData {
  user: User;
}

@Component({
  selector: 'app-user-roles-inline-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule
],
  templateUrl: './user-roles-inline-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-roles-inline-dialog.component.scss'],
})
export class UserRolesInlineDialogComponent implements OnInit, OnDestroy {

  user: User;
  applications: Application[] = [];
  filteredApplications: Application[] = [];
  appSearchTerm = '';
  isLoading = false;
  isSaving = false;

  roleSelection: RoleSelection = {};
  pendingChanges: PendingChange[] = [];
  private initialRoleSelection: RoleSelection = {};

  /** Profil de l'admin connecté, résolu une seule fois à l'init */
  private adminProfile: AdminProfile = null;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserRolesDialogData,
    private dialogRef: MatDialogRef<UserRolesInlineDialogComponent>,
    private roleService: RoleService,
    private clientService: ClientService,
    private notificationService: NotificationService,
    private rolePermissionService: RolePermissionService,
    private snackBar: MatSnackBar
  ) {
    this.user = data.user;
  }

  ngOnInit(): void {
    // Résolution du profil admin une seule fois pour toute la durée de vie du dialog
    this.adminProfile = this.rolePermissionService.getAdminProfile();
    this.loadApplicationsAndRoles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Chargement principal — remplace l'ancienne chaîne getClientsByUser()
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Détermine les types à demander au backend selon le profil admin, puis
   * appelle /clients/by-types/with-roles qui retourne déjà clients + rôles
   * filtrés en un seul appel.
   *
   *   IES_ADMIN   → types = ['external-app']
   *   SUPER_ADMIN → types = ['external-app', 'internal-app']
   */
  private loadApplicationsAndRoles(): void {
    if (!this.adminProfile) {
      this.showError('Profil administrateur non reconnu. Accès refusé.');
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    const types: string[] =
      this.adminProfile === 'SCA_SUPER_ADMIN'
        ? ['external-app', 'internal-app']
        : ['external-app'];

    this.clientService
      .getClientsByTypesWithRoles(types)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (clientsWithRoles) => {
          /**
           * Le backend renvoie des ClientRolesTDO :
           *   { id, clientId, roles: [{ id, name }] }
           *
           * On mappe directement vers Application en utilisant les rôles déjà filtrés.
           */
          this.applications = clientsWithRoles.map(c => ({
            id: c.id,
            name: c.clientId,
            description: '',
            icon: this.getAppIcon(c.clientId),
            // Les rôles proviennent du backend, déjà filtrés selon le profil
            roles: c.roles.map(r => ({ id: r.id, name: r.name, title: r.title } as Role)),
            expanded: false,
          }));

          if (this.applications.length === 0) {
            this.filteredApplications = [];
            this.isLoading = false;
            return;
          }

          // Charger les rôles actuellement attribués à l'utilisateur
          this.loadUserCurrentRoles();
        },
        error: (err) => {
          console.error('Erreur chargement clients+rôles:', err);
          this.showError('Erreur lors du chargement des applications');
          this.isLoading = false;
        },
      });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Rôles actuels de l'utilisateur (pré-cochage des cases)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Pour chaque application chargée, interroge le backend pour savoir quels
   * rôles sont déjà attribués à l'utilisateur, puis pré-coche les cases
   * correspondantes.
   *
   * Seuls les rôles présents dans `app.roles` (déjà filtrés) sont considérés :
   * un rôle attribué mais non visible pour l'admin connecté n'est pas affiché
   * et ne sera donc jamais modifié par ce dialog.
   */
  private loadUserCurrentRoles(): void {
    const userRoleRequests = this.applications.map(app =>
      this.roleService.getUserRoles(this.user.id, app.id).pipe(
        catchError(() => of(null))   // on tolère l'échec pour un client isolé
      )
    );

    forkJoin(userRoleRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          this.initializeRoleSelection();
          this.markUserRolesAsSelected(responses);

          // Auto-expand les apps qui ont déjà des rôles attribués
          this.applications.forEach(app => {
            app.expanded = this.getSelectedRolesCount(app) > 0;
          });

          this.updateFilteredApplications();
          this.isLoading = false;
        },
        error: () => {
          // Cas très rare (forkJoin propage seulement si catchError a aussi planté)
          this.initializeRoleSelection();
          this.updateFilteredApplications();
          this.isLoading = false;
        },
      });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Initialisation de la sélection de rôles
  // ─────────────────────────────────────────────────────────────────────────────

  private initializeRoleSelection(): void {
    this.roleSelection = {};
    this.initialRoleSelection = {};

    this.applications.forEach(app => {
      if (app.roles && app.roles.length > 0) {
        this.roleSelection[app.id] = {};
        this.initialRoleSelection[app.id] = {};
        app.roles.forEach(role => {
          this.roleSelection[app.id][role.id] = false;
          this.initialRoleSelection[app.id][role.id] = false;
        });
      }
    });
  }

  /**
   * Pré-coche les rôles de l'utilisateur.
   *
   * Ne coche QUE les rôles qui sont dans `roleSelection` (i.e. ceux que l'admin
   * peut voir). Les rôles attribués à l'utilisateur mais hors du périmètre de
   * l'admin connecté sont ignorés et laissés intacts côté backend.
   */
  private markUserRolesAsSelected(userRolesResponses: (any | null)[]): void {
    userRolesResponses.forEach((userRoles, index) => {
      if (!userRoles) return;   // appel en erreur toléré

      const appId = this.applications[index].id;
      const rolesArray = this.extractRolesArray(userRoles);

      rolesArray.forEach((userRole: any) => {
        const roleId = userRole.id || userRole.roleId;
        if (
          roleId &&
          this.roleSelection[appId] &&
          this.roleSelection[appId][roleId] !== undefined
        ) {
          this.roleSelection[appId][roleId] = true;
          this.initialRoleSelection[appId][roleId] = true;
        }
        // Si le roleId n'est pas dans roleSelection, l'admin n'y a pas accès :
        // on l'ignore silencieusement.
      });
    });
  }

  private extractRolesArray(userRoles: any): any[] {
    if (!userRoles) return [];
    if (Array.isArray(userRoles)) return userRoles;
    if (userRoles.content && Array.isArray(userRoles.content)) return userRoles.content;
    if (userRoles.roles && Array.isArray(userRoles.roles)) return userRoles.roles;
    if (userRoles.realmMappings && Array.isArray(userRoles.realmMappings)) return userRoles.realmMappings;
    return [];
  }

  private updateFilteredApplications(): void {
    this.filterApplications();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Recherche
  // ─────────────────────────────────────────────────────────────────────────────

  onSearchChange(term: string): void {
    this.appSearchTerm = term;
    this.filterApplications();
  }

  private filterApplications(): void {
    const term = this.appSearchTerm.toLowerCase().trim();
    this.filteredApplications = term
      ? this.applications.filter(a =>
          a.name.toLowerCase().includes(term) ||
          a.description?.toLowerCase().includes(term)
        )
      : [...this.applications];
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Toggle expand
  // ─────────────────────────────────────────────────────────────────────────────

  toggleApp(app: Application): void {
    app.expanded = !app.expanded;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Logique de sélection des rôles
  // ─────────────────────────────────────────────────────────────────────────────

  isRoleSelected(app: Application, role: Role): boolean {
    return !!(this.roleSelection[app.id]?.[role.id]);
  }

  isAppFullySelected(app: Application): boolean {
    if (!app.roles?.length || !this.roleSelection[app.id]) return false;
    return app.roles.every(r => this.roleSelection[app.id][r.id]);
  }

  isAppPartiallySelected(app: Application): boolean {
    if (!app.roles?.length || !this.roleSelection[app.id]) return false;
    const count = app.roles.filter(r => this.roleSelection[app.id][r.id]).length;
    return count > 0 && count < app.roles.length;
  }

  toggleAppSelection(app: Application, event: any): void {
    if (!this.roleSelection[app.id]) this.roleSelection[app.id] = {};
    app.roles?.forEach(role => {
      this.roleSelection[app.id][role.id] = event.checked;
    });
    this.updatePendingChanges();
  }

  toggleRoleSelection(app: Application, role: Role, event: any): void {
    if (!this.roleSelection[app.id]) this.roleSelection[app.id] = {};
    this.roleSelection[app.id][role.id] = event.checked;
    this.updatePendingChanges();
  }

  selectAllAppRoles(app: Application): void {
    if (!this.roleSelection[app.id]) this.roleSelection[app.id] = {};
    const allSelected = this.isAppFullySelected(app);
    app.roles?.forEach(role => {
      this.roleSelection[app.id][role.id] = !allSelected;
    });
    this.updatePendingChanges();
  }

  getSelectedRolesCount(app: Application): number {
    if (!app.roles || !this.roleSelection[app.id]) return 0;
    return app.roles.filter(r => this.roleSelection[app.id][r.id]).length;
  }

  getTotalSelectedRoles(): number {
    return Object.values(this.roleSelection)
      .flatMap(appRoles => Object.values(appRoles))
      .filter(Boolean).length;
  }

  getTotalRoles(): number {
    return this.applications.reduce((t, a) => t + (a.roles?.length || 0), 0);
  }

  private updatePendingChanges(): void {
    this.pendingChanges = [];
    Object.keys(this.roleSelection).forEach(appId => {
      Object.keys(this.roleSelection[appId]).forEach(roleId => {
        const current = this.roleSelection[appId][roleId];
        const initial = this.initialRoleSelection[appId]?.[roleId];
        if (current !== initial) {
          this.pendingChanges.push({
            appId,
            roleId,
            action: current ? 'assign' : 'unassign',
            initialState: !!initial,
            currentState: current,
          });
        }
      });
    });
  }

  get hasChanges(): boolean {
    return this.pendingChanges.length > 0;
  }

  resetAll(): void {
    Object.keys(this.initialRoleSelection).forEach(appId => {
      Object.keys(this.initialRoleSelection[appId]).forEach(roleId => {
        if (this.roleSelection[appId]) {
          this.roleSelection[appId][roleId] = this.initialRoleSelection[appId][roleId];
        }
      });
    });
    this.updatePendingChanges();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Sauvegarde / Annulation
  // ─────────────────────────────────────────────────────────────────────────────

  onSave(): void {
    if (!this.hasChanges) return;
    this.isSaving = true;

    const requests = this.pendingChanges.map(change =>
      change.action === 'assign'
        ? this.roleService.assignClientRole(this.user.id, change.roleId, change.appId)
        : this.roleService.unassignClientRole(this.user.id, change.roleId, change.appId)
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving = false;
        this.notificationService.showSuccess({
          title: 'Succès',
          message: 'Rôles mis à jour avec succès',
          duration: 4000,
          showClose: true,
        });
        // Mettre à jour l'état initial pour refléter le nouvel état sauvegardé
        this.initialRoleSelection = JSON.parse(JSON.stringify(this.roleSelection));
        this.updatePendingChanges();
        this.dialogRef.close({ modified: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.showError('Erreur lors de la sauvegarde : ' + (error.message || 'Erreur inconnue'));
      },
    });
  }

  onCancel(): void {
    if (this.hasChanges) {
      if (!confirm('Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter ?')) {
        return;
      }
    }
    this.dialogRef.close({ modified: false });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Utilitaires
  // ─────────────────────────────────────────────────────────────────────────────

  getUserInitials(firstName?: string, lastName?: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || '?';
  }

  getAvatarColor(username?: string): string {
    const colors = ['#45B56F', '#4ECDC4', '#45B7D1', '#96CEB4', '#FF6B6B', '#DDA0DD'];
    return colors[(username?.length || 0) % colors.length];
  }

  private getAppIcon(clientId: string): string {
    const icons: Record<string, string> = {
      webapp: 'language',
      mobile: 'smartphone',
      admin: 'admin_panel_settings',
      api: 'api',
    };
    return icons[clientId?.toLowerCase()] || 'apps';
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }
}